import React, { useState, useEffect, useRef } from 'react';
import { X, CheckCircle, XCircle, Clock } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { khqrApi } from '../../api';
import { useApp } from '../../context/AppContext';

const KhqrPaymentDialog = ({ amount, orderNo, onSuccess, onCancel }) => {
  const { addWsListener } = useApp();
  const [currency, setCurrency] = useState('USD');
  const [status, setStatus]     = useState('idle');
  const [qrData, setQrData]     = useState(null);
  const [timeLeft, setTimeLeft] = useState(300);
  const pollRef  = useRef(null);
  const timerRef = useRef(null);

  // 清理定时器
  useEffect(() => {
    return () => {
      clearInterval(pollRef.current);
      clearInterval(timerRef.current);
    };
  }, []);

  // 监听 WS 消息
  useEffect(() => {
    if (!orderNo) return;

    const unsub = addWsListener((msg) => {
      if (msg.type === 'KHQR_PAID' && msg.data?.khqr_order_no === orderNo) {
        clearInterval(pollRef.current);
        clearInterval(timerRef.current);
        setStatus('success');
        setTimeout(() => onSuccess(msg.data), 1500);
      }
      if (msg.type === 'KHQR_FAILED' && msg.data?.order_no === orderNo) {
        clearInterval(pollRef.current);
        clearInterval(timerRef.current);
        setStatus(msg.data.status === 'timeout' ? 'timeout' : 'failed');
      }
    });

    return unsub;
  }, [orderNo, addWsListener, onSuccess]);

  const createQr = async (selectedCurrency = currency) => {
    clearInterval(pollRef.current);
    clearInterval(timerRef.current);
    setStatus('loading');
    setQrData(null);
    setTimeLeft(300);

    try {
      const res = await khqrApi.create({
        order_no: orderNo,
        amount,
        currency: selectedCurrency
      });
      if (res.success) {
        setQrData(res.data);
        setStatus('waiting');
        startPolling(orderNo);
        startTimer();
      } else {
        setStatus('failed');
      }
    } catch (err) {
      console.error('创建KHQR失败:', err);
      setStatus('failed');
    }
  };

  // 兜底轮询，30秒一次
  const startPolling = (order_no) => {
    pollRef.current = setInterval(async () => {
      try {
        const res = await khqrApi.check({ order_no });
        if (res.success) {
          const s = res.data?.status;
          if (s === 'success') {
            clearInterval(pollRef.current);
            clearInterval(timerRef.current);
            setStatus('success');
            setTimeout(() => onSuccess(), 1500);
          } else if (s === 'failed' || s === 'timeout') {
            clearInterval(pollRef.current);
            clearInterval(timerRef.current);
            setStatus(s);
          }
        }
      } catch (err) {
        console.error('轮询失败:', err);
      }
    }, 30000);
  };

  const startTimer = () => {
    timerRef.current = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          clearInterval(pollRef.current);
          setStatus('timeout');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const formatTime = (secs) => {
    const m = Math.floor(secs / 60).toString().padStart(2, '0');
    const s = (secs % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  const handleCurrencyChange = (c) => {
    setCurrency(c);
    if (status === 'waiting' || status === 'failed' || status === 'timeout') {
      createQr(c);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-sm shadow-2xl">

        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="font-bold text-gray-800">扫码支付</h3>
          <button onClick={onCancel} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 text-center">

          {/* 支付方式切换 */}
          {(status === 'idle' || status === 'waiting' || status === 'failed' || status === 'timeout') && (
            <div className="flex rounded-lg border border-gray-200 overflow-hidden mb-4">
              <button onClick={() => handleCurrencyChange('USD')}
                className={`flex-1 py-2 text-sm font-medium transition-colors ${
                  currency === 'USD' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}>
                KHQR (USD)
              </button>
              <button onClick={() => handleCurrencyChange('KHR')}
                className={`flex-1 py-2 text-sm font-medium transition-colors ${
                  currency === 'KHR' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}>
                支付宝 (KHR)
              </button>
            </div>
          )}

          {/* 金额 */}
          <div className="mb-4">
            <p className="text-gray-500 text-sm">支付金额</p>
            <p className="text-3xl font-bold text-blue-600">${parseFloat(amount).toFixed(2)}</p>
            {currency === 'KHR' && qrData?.khr_rate && (
              <p className="text-sm text-gray-400 mt-1">
                ≈ {(parseFloat(amount) * qrData.khr_rate).toLocaleString()} ៛
                <span className="ml-1 text-xs">(汇率 {qrData.khr_rate})</span>
              </p>
            )}
          </div>

          {/* 初始状态 */}
          {status === 'idle' && (
            <div className="py-6">
              <button onClick={() => createQr(currency)}
                className="w-full py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700">
                生成二维码
              </button>
            </div>
          )}

          {/* 加载中 */}
          {status === 'loading' && (
            <div className="py-12">
              <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
              <p className="text-gray-500">正在生成二维码...</p>
            </div>
          )}

          {/* 等待扫码 */}
          {status === 'waiting' && qrData && (
            <>
              <div className="flex justify-center my-2">
                <div className="bg-white p-3 rounded-xl border border-gray-200 shadow-sm">
                  <QRCodeSVG value={qrData.qr_string} size={200} level="M" includeMargin={false} />
                </div>
              </div>
              <div className="mt-3 flex items-center justify-center gap-2 text-gray-500">
                <Clock className="w-4 h-4" />
                <span className="text-sm">有效期 <span className={`font-bold ${timeLeft < 60 ? 'text-red-500' : 'text-gray-700'}`}>{formatTime(timeLeft)}</span></span>
              </div>
              <p className="text-xs text-gray-400 mt-2">
                {currency === 'KHR' ? '请使用支付宝扫码支付' : '请使用 ABA / Wing / ACLEDA 扫码支付'}
              </p>
            </>
          )}

          {/* 支付成功 */}
          {status === 'success' && (
            <div className="py-8">
              <CheckCircle className="w-20 h-20 text-green-500 mx-auto mb-4" />
              <p className="text-xl font-bold text-green-600">支付成功！</p>
            </div>
          )}

          {/* 支付失败 */}
          {status === 'failed' && (
            <div className="py-8">
              <XCircle className="w-20 h-20 text-red-500 mx-auto mb-4" />
              <p className="text-xl font-bold text-red-600">支付失败</p>
              <button onClick={() => createQr(currency)}
                className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-lg text-sm">
                重新生成
              </button>
            </div>
          )}

          {/* 已超时 */}
          {status === 'timeout' && (
            <div className="py-8">
              <Clock className="w-20 h-20 text-gray-400 mx-auto mb-4" />
              <p className="text-xl font-bold text-gray-600">二维码已过期</p>
              <button onClick={() => createQr(currency)}
                className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-lg text-sm">
                重新生成
              </button>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default KhqrPaymentDialog;